import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MailWarning } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAdminApi } from "@/lib/api";
import { InquiryDetail } from "@/admin/InquiryDetail";
import { StatusSelect, STATUSES } from "@/admin/StatusSelect";

const Filters = ({ counts, value, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid="inquiry-filters">
    {["All", ...STATUSES].map((s) => {
      const n = s === "All" ? Object.values(counts).reduce((a, b) => a + b, 0) : counts[s] || 0;
      return (
        <button key={s} onClick={() => onChange(s)} data-testid={`inquiry-filter-${s.toLowerCase()}`}
          className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${value === s ? "bg-emerald-900 text-white border-emerald-900" : "bg-white border-stone-200 hover:bg-stone-100"}`}>
          {s} <span className="opacity-70">{n}</span>
        </button>
      );
    })}
  </div>
);

export default function AdminInquiries() {
  const call = useAdminApi();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("All");
  const [openId, setOpenId] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["inquiries", filter],
    queryFn: () => call({ url: "/admin/inquiries", params: filter === "All" ? {} : { status: filter } }),
  });
  const items = data?.items || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["inquiries"] });
  const open = items.find((i) => i.id === openId);

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Inquiries</h1>
        <p className="text-sm text-stone-500">Call each buyer, then move the status along.</p>
      </div>
      <Filters counts={data?.counts || {}} value={filter} onChange={setFilter} />
      <div className="bg-white border border-stone-200 rounded-md overflow-x-auto">
        <Table data-testid="inquiry-table">
          <TableHeader>
            <TableRow>
              <TableHead>Received</TableHead><TableHead>Company</TableHead><TableHead>Product</TableHead>
              <TableHead>Qty / Port</TableHead><TableHead>Phone</TableHead><TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={6} className="text-stone-500">Loading...</TableCell></TableRow>}
            {!isLoading && !items.length && <TableRow><TableCell colSpan={6} className="text-stone-500" data-testid="inquiry-empty">No inquiries yet.</TableCell></TableRow>}
            {items.map((i) => (
              <TableRow key={i.id} className="cursor-pointer" onClick={() => setOpenId(i.id)} data-testid={`inquiry-row-${i.id}`}>
                <TableCell className="whitespace-nowrap text-xs text-stone-500">{new Date(i.created_at).toLocaleString()}</TableCell>
                <TableCell>
                  <p className="font-medium flex items-center gap-1.5">{i.company}
                    {i.email_status === "failed" && <MailWarning className="h-3.5 w-3.5 text-amber-700" title="Email alert failed" />}</p>
                  <p className="text-xs text-stone-500">{i.country}</p>
                </TableCell>
                <TableCell>{i.product_name} <span className="text-xs text-stone-500">{i.incoterm}</span></TableCell>
                <TableCell className="text-sm">{i.quantity}<p className="text-xs text-stone-500">{i.destination_port}</p></TableCell>
                <TableCell className="whitespace-nowrap text-sm">{i.phone}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}><StatusSelect inquiry={i} onDone={refresh} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <InquiryDetail inquiry={open} onClose={() => setOpenId(null)} onChanged={refresh} />
    </div>
  );
}
